import React, { useState, useEffect } from "react";
import { X, AlertTriangle } from "lucide-react";
import type { BikeWithLocation } from "../../types/bike";
import { useVehicleCategoryStore } from "../../store/useVehicleCategoryStore";
import { useCityStore } from "../../store/useCityStore";
import { useAuth } from "../../hooks/useAuth";
import { apiService } from "../../services/api";
import type { Fleet } from "../../types/auth";

interface BikeModalProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit" | "delete";
  bike?: BikeWithLocation | null;
  onSave: (bikeData: Record<string, unknown>) => Promise<void>;
}

export const BikeModal: React.FC<BikeModalProps> = ({
  open,
  onClose,
  mode,
  bike,
  onSave,
}) => {
  const { categories, loadIfNeeded: loadCategoriesIfNeeded } =
    useVehicleCategoryStore();
  const { cities, loadIfNeeded: loadCitiesIfNeeded } = useCityStore();
  const { isAdmin } = useAuth();
  const [fleets, setFleets] = useState<Fleet[]>([]);
  const [formData, setFormData] = useState({
    bike_id: "",
    status: "active" as "active" | "inactive" | "maintenance",
    category_id: "" as string,
    bike_sn: "",
    max_speed: "",
    comment: "",
    primary_city_id: "",
    fleet_id: "",
    ui_hidden: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Reset form when modal opens/closes or bike changes
  useEffect(() => {
    if (open) {
      if (mode === "create") {
        setFormData({
          bike_id: "",
          status: "active",
          category_id: "",
          bike_sn: "",
          max_speed: "",
          comment: "",
          primary_city_id: "",
          fleet_id: "",
          ui_hidden: false,
        });
      } else if (bike && (mode === "edit" || mode === "delete")) {
        setFormData({
          bike_id: bike.bike_id,
          status: bike.status,
          category_id:
            bike.category_id !== undefined && bike.category_id !== null
              ? String(bike.category_id)
              : "",
          bike_sn:
            bike.bike_sn !== undefined && bike.bike_sn !== null
              ? String(bike.bike_sn)
              : "",
          max_speed:
            bike.max_speed !== undefined && bike.max_speed !== null
              ? String(bike.max_speed)
              : "",
          comment:
            bike.comment !== undefined && bike.comment !== null
              ? String(bike.comment)
              : "",
          primary_city_id:
            bike.primary_city_id !== undefined && bike.primary_city_id !== null
              ? String(bike.primary_city_id)
              : "",
          fleet_id: bike.fleet_id ? String(bike.fleet_id) : "",
          ui_hidden: Boolean(bike.ui_hidden),
        });
      }
      setError("");
    }
  }, [open, mode, bike]);

  useEffect(() => {
    if (!open) return;
    loadCategoriesIfNeeded().catch(console.error);
    loadCitiesIfNeeded().catch(console.error);
    if (isAdmin) {
      apiService
        .getFleets()
        .then((response) => {
          setFleets((response as { fleets: Fleet[] }).fleets || []);
        })
        .catch(console.error);
    }
  }, [open, loadCategoriesIfNeeded, loadCitiesIfNeeded, isAdmin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (mode === "create" || mode === "edit") {
      // Validate form
      if (!formData.bike_id.trim()) {
        setError("Bike ID is required");
        return;
      }
      if (
        formData.max_speed.trim() &&
        (!Number.isFinite(Number(formData.max_speed)) ||
          Number(formData.max_speed) < 0)
      ) {
        setError("Max speed must be a positive number");
        return;
      }
    }

    const maxSpeed =
      formData.max_speed.trim().length > 0 ? Number(formData.max_speed) : null;

    setLoading(true);
    try {
      if (mode === "delete") {
        await onSave({});
      } else if (mode === "edit") {
        await onSave({
          status: formData.status,
          category_id: formData.category_id ? formData.category_id : null,
          bike_sn: formData.bike_sn.trim() ? formData.bike_sn.trim() : null,
          max_speed: maxSpeed,
          comment: formData.comment.trim() ? formData.comment.trim() : null,
          primary_city_id: formData.primary_city_id
            ? formData.primary_city_id
            : null,
          ui_hidden: formData.ui_hidden,
          ...(isAdmin ? { fleet_id: formData.fleet_id || null } : {}),
        });
      } else {
        await onSave({
          bike_id: formData.bike_id.trim(),
          status: formData.status,
          category_id: formData.category_id ? formData.category_id : null,
          bike_sn: formData.bike_sn.trim() ? formData.bike_sn.trim() : null,
          max_speed: maxSpeed,
          comment: formData.comment.trim() ? formData.comment.trim() : null,
          primary_city_id: formData.primary_city_id
            ? formData.primary_city_id
            : null,
          ui_hidden: formData.ui_hidden,
          ...(isAdmin ? { fleet_id: formData.fleet_id || null } : {}),
        });
      }
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError("");
  };

  if (!open) return null;

  const getModalTitle = () => {
    switch (mode) {
      case "create":
        return "Add New Bike";
      case "edit":
        return "Edit Bike";
      case "delete":
        return "Delete Bike";
      default:
        return "";
    }
  };

  const getSubmitButtonText = () => {
    if (loading) return "Processing...";
    switch (mode) {
      case "create":
        return "Add Bike";
      case "edit":
        return "Save Changes";
      case "delete":
        return "Delete Bike";
      default:
        return "Save";
    }
  };

  const getSubmitButtonClass = () => {
    const baseClass =
      "inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

    if (mode === "delete") {
      return `${baseClass} text-white bg-red-600 hover:bg-red-700 focus:ring-red-500`;
    }
    return `${baseClass} text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500`;
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Center modal on screen */}
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {getModalTitle()}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                  <span className="text-sm text-red-600">{error}</span>
                </div>
              </div>
            )}

            {/* Content */}
            {mode === "delete" ? (
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete the bike{" "}
                      <span className="font-semibold">{bike?.bike_id}</span>?
                      This action cannot be undone and will remove all
                      associated GPS data.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Bike ID */}
                <div>
                  <label
                    htmlFor="bike_id"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Bike ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="bike_id"
                    value={formData.bike_id}
                    onChange={(e) =>
                      handleInputChange("bike_id", e.target.value)
                    }
                    disabled={mode === "edit"} // Can't change bike ID when editing
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Enter unique bike ID"
                  />
                  {mode === "edit" && (
                    <p className="mt-1 text-xs text-gray-500">
                      Bike ID cannot be changed after creation
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="category_id"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Category
                  </label>
                  <select
                    id="category_id"
                    value={formData.category_id}
                    onChange={(e) =>
                      handleInputChange("category_id", e.target.value)
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Uncategorized</option>
                    {categories.map((category) => (
                      <option
                        key={category.category_id}
                        value={category.category_id}
                      >
                        {category.category_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="bike_sn"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Bike SN
                  </label>
                  <input
                    type="text"
                    id="bike_sn"
                    value={formData.bike_sn}
                    onChange={(e) =>
                      handleInputChange("bike_sn", e.target.value)
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter bike serial number"
                  />
                </div>

                <div>
                  <label
                    htmlFor="max_speed"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Max Speed
                  </label>
                  <input
                    type="number"
                    id="max_speed"
                    min="0"
                    step="1"
                    value={formData.max_speed}
                    onChange={(e) =>
                      handleInputChange("max_speed", e.target.value)
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter max speed"
                  />
                </div>

                <div>
                  <label
                    htmlFor="primary_city_id"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Main City
                  </label>
                  <select
                    id="primary_city_id"
                    value={formData.primary_city_id}
                    onChange={(e) =>
                      handleInputChange("primary_city_id", e.target.value)
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">No main city</option>
                    {cities.map((city) => (
                      <option key={city.city_id} value={city.city_id}>
                        {city.city_name} ({city.country_code})
                      </option>
                    ))}
                  </select>
                </div>

                {isAdmin && (
                  <div>
                    <label
                      htmlFor="fleet_id"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Fleet
                    </label>
                    <select
                      id="fleet_id"
                      value={formData.fleet_id}
                      onChange={(e) =>
                        handleInputChange("fleet_id", e.target.value)
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Unassigned</option>
                      {fleets.map((fleet) => (
                        <option key={fleet.id} value={fleet.id}>
                          {fleet.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="comment"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Comment
                  </label>
                  <textarea
                    id="comment"
                    value={formData.comment}
                    onChange={(e) =>
                      handleInputChange("comment", e.target.value)
                    }
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Optional comment"
                  />
                </div>

                {/* Hidden from tracker */}
                <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                  <input
                    type="checkbox"
                    id="ui_hidden"
                    checked={formData.ui_hidden}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        ui_hidden: e.target.checked,
                      }))
                    }
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div>
                    <label
                      htmlFor="ui_hidden"
                      className="text-sm font-medium text-gray-700 cursor-pointer"
                    >
                      Hidden from Vehicle Tracker
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      When checked, this vehicle does not appear in maps,
                      dashboards, reports, or selectors. Data is kept in the
                      database.
                    </p>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label
                    htmlFor="status"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Status
                  </label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) =>
                      handleInputChange("status", e.target.value)
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={getSubmitButtonClass()}
            >
              {getSubmitButtonText()}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:mr-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
